import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../config/api';
import { FiDownload, FiEdit2, FiX } from 'react-icons/fi';

const MonthlyBookingReport = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Inline edit modal for correcting amount_paid on legacy/wrong-data rows
  const [editing, setEditing] = useState(null); // {booking_id, amount_paid, grand_total, guest_name}
  const [editAmount, setEditAmount] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const parsed = JSON.parse(stored);
    if (!parsed.is_admin) { navigate('/dashboard'); return; }
  }, [navigate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/monthly-report?month=${month}&year=${year}`);
      setReport(res.data);
    } catch (err) {
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/admin/monthly-report.pdf?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RGV-Revenue-${year}-${String(month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      window.alert('PDF download failed. Please try again.');
    }
    setDownloading(false);
  };

  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const openEdit = (entry) => {
    setEditing({
      booking_id: entry.booking_id,
      grand_total: entry.grand_total,
      guest_name: entry.guest_name,
      villa_name: entry.villa_name,
      current_paid: entry.amount,
    });
    setEditAmount(String(entry.amount || 0));
    setEditError('');
  };

  const saveEdit = async () => {
    setEditError('');
    const amt = parseInt(editAmount, 10);
    if (Number.isNaN(amt) || amt < 0) { setEditError('Enter a valid amount (Rs.0 or more)'); return; }
    if (amt > editing.grand_total) { setEditError(`Cannot exceed grand total Rs.${editing.grand_total.toLocaleString()}`); return; }
    setEditBusy(true);
    try {
      await api.post(`/admin/bookings/${editing.booking_id}/update-totals`, { amount_paid: amt });
      setEditing(null);
      await fetchReport();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Update failed');
    }
    setEditBusy(false);
  };

  return (
    <div data-testid="monthly-report-page" className="pt-20 min-h-screen bg-[#0A0E17] text-white">
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
            <div>
              <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-3">Admin · Website-paid bookings only</p>
              <h1 className="text-3xl md:text-4xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Monthly <span className="text-[#F57C20]">Report</span>
              </h1>
              <p className="text-white/40 text-xs mt-2 max-w-2xl">
                Includes only bookings completed and paid via the website (Razorpay).
                Offline / Airbnb / abandoned-cart entries are excluded.
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <select
                value={month}
                onChange={e => setMonth(parseInt(e.target.value))}
                data-testid="report-month-select"
                className="bg-white/5 border border-white/10 px-4 py-2 text-white text-sm font-light focus:outline-none focus:border-[#F57C20]/40"
              >
                {months.map((m, i) => (
                  <option key={i} value={i + 1} className="bg-[#131722]">{m}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={e => setYear(parseInt(e.target.value))}
                data-testid="report-year-select"
                className="bg-white/5 border border-white/10 px-4 py-2 text-white text-sm font-light focus:outline-none focus:border-[#F57C20]/40"
              >
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y} className="bg-[#131722]">{y}</option>
                ))}
              </select>
              <button
                onClick={handleDownloadPdf}
                disabled={downloading || loading}
                data-testid="download-report-pdf"
                className="bg-[#F57C20] text-white px-4 py-2 text-sm font-semibold rounded-md hover:bg-[#E06A10] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <FiDownload className="w-4 h-4" /> {downloading ? 'Generating...' : 'Download PDF'}
              </button>
            </div>
          </div>

          {/* Revenue Summary Banner */}
          {report && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-[#1B2432] border border-white/5 rounded-xl p-5" data-testid="report-summary-month">
                <p className="text-white/50 text-[11px] tracking-wider uppercase mb-1">Month</p>
                <p className="text-white text-xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>{report.month_name}</p>
              </div>
              <div className="bg-[#1B2432] border border-white/5 rounded-xl p-5" data-testid="report-summary-bookings">
                <p className="text-white/50 text-[11px] tracking-wider uppercase mb-1">Website Bookings</p>
                <p className="text-white text-2xl font-light">{report.total_bookings}</p>
              </div>
              <div className="bg-gradient-to-br from-[#F57C20]/15 to-[#1B2432] border border-[#F57C20]/30 rounded-xl p-5" data-testid="report-summary-revenue">
                <p className="text-[#F57C20] text-[11px] tracking-wider uppercase mb-1 font-semibold">Revenue Collected</p>
                <p className="text-white text-2xl font-bold">Rs.{report.total_revenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="shimmer h-64" />
          ) : report ? (
            <>
              {report.entries?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="report-table">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Check-in</th>
                        <th className="text-left py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Villa</th>
                        <th className="text-left py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Guest</th>
                        <th className="text-left py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Phone</th>
                        <th className="text-left py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Nights</th>
                        <th className="text-right py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Paid</th>
                        <th className="text-right py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Total</th>
                        <th className="text-left py-3 px-4 text-white/40 font-light text-xs tracking-wider uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.entries.map((entry, i) => {
                        // Derive status from actual paid vs total (don't trust
                        // stored payment_status, which may be 'paid' even when
                        // only 50% advance is collected).
                        const paid = Number(entry.amount || 0);
                        const total = Number(entry.grand_total || 0);
                        let derived = (entry.payment_status || 'pending').toLowerCase();
                        if (total > 0) {
                          if (paid <= 0) derived = 'pending';
                          else if (paid >= total) derived = 'completed';
                          else derived = 'partial';
                        }
                        return (
                          <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                            <td className="py-3 px-4 text-white/70 font-light">{entry.check_in}</td>
                            <td className="py-3 px-4 text-white/70 font-light">{entry.villa_name}</td>
                            <td className="py-3 px-4 text-white font-light">{entry.guest_name}</td>
                            <td className="py-3 px-4 text-white/50 font-light">{entry.guest_phone}</td>
                            <td className="py-3 px-4 text-white/70 font-light">{entry.nights}</td>
                            <td className="py-3 px-4 text-[#F57C20] font-semibold text-right">Rs.{paid.toLocaleString()}</td>
                            <td className="py-3 px-4 text-white/50 text-right">Rs.{total.toLocaleString() || '-'}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  derived === 'completed' ? 'bg-green-500/10 text-green-400' :
                                  derived === 'partial' ? 'bg-yellow-500/10 text-yellow-400' :
                                  'bg-white/5 text-white/40'
                                }`}>
                                  {derived}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => openEdit(entry)}
                                  title="Correct paid amount"
                                  data-testid={`edit-paid-${entry.booking_id?.slice(0, 8) || i}`}
                                  className="text-white/30 hover:text-[#F57C20] transition-colors"
                                >
                                  <FiEdit2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 text-white/30 font-light">
                  No website-paid bookings found for this month.
                </div>
              )}
            </>
          ) : null}
        </div>
      </section>

      {/* Inline edit modal: correct paid amount for legacy / wrongly-marked bookings */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-paid-modal">
          <div className="bg-[#1B2432] border border-white/10 rounded-xl max-w-md w-full p-6 relative">
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="absolute top-3 right-3 text-white/40 hover:text-white"
              data-testid="edit-paid-close"
            >
              <FiX className="w-5 h-5" />
            </button>

            <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-2">Correct Paid Amount</p>
            <h3 className="text-white text-lg font-light mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              {editing.guest_name}
            </h3>
            <p className="text-white/40 text-xs mb-5">{editing.villa_name} · Booking #{editing.booking_id?.slice(0,8).toUpperCase()}</p>

            <div className="bg-white/5 rounded-lg p-3 mb-4 text-xs">
              <div className="flex justify-between text-white/60 mb-1"><span>Grand Total</span><span className="text-white">Rs.{editing.grand_total.toLocaleString()}</span></div>
              <div className="flex justify-between text-white/60"><span>Currently marked paid</span><span className="text-white">Rs.{editing.current_paid.toLocaleString()}</span></div>
            </div>

            <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Actual amount paid (Rs.) *</label>
            <input
              type="number"
              value={editAmount}
              onChange={e => setEditAmount(e.target.value)}
              autoFocus
              data-testid="edit-paid-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-base focus:outline-none focus:border-[#F57C20] mb-1"
              placeholder={`e.g. ${Math.round(editing.grand_total / 2)} for 50% advance`}
            />
            <p className="text-white/30 text-[10px] mb-4">
              Tip: for 50% advance enter Rs.{Math.round(editing.grand_total / 2).toLocaleString()}. Status auto-updates to 'partial' / 'completed' based on this value.
            </p>

            {editError && <p className="text-red-400 text-xs mb-3" data-testid="edit-paid-error">{editError}</p>}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="bg-white/5 text-white/70 hover:text-white border border-white/10 px-4 py-2 rounded-lg text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editBusy}
                data-testid="edit-paid-save"
                className="bg-[#F57C20] text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-[#e06b15] disabled:opacity-50"
              >
                {editBusy ? 'Saving…' : 'Save & Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyBookingReport;
